import { useCallback, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { TooltipProvider } from '../ui/primitives'
import { CommandPalette, useCommandPalette } from './CommandPalette'
import { RouteBreadcrumbs } from './RouteBreadcrumbs'
import { Sidebar, type NavSection } from './Sidebar'
import { TopNav } from './TopNav'

const COLLAPSE_KEY = 'helpdesk.navCollapsed'

/** The application frame: sidebar, top bar, breadcrumb strip, content well.
 *
 * The rail is a static column at `lg` and above and a drawer below it, so the
 * same tree serves desktop, laptop, tablet and phone without a second layout.
 *
 * `scopeControl` and `notifications` are slots rather than built in, because
 * both are agent-console concepts and this shell is not.
 */
export function AppShell({
  nav,
  user,
  scopeControl,
  notifications,
  children,
}: {
  nav: NavSection[]
  user: { name: string; email: string; roles?: string[] }
  scopeControl?: ReactNode
  notifications?: ReactNode
  children: ReactNode
}) {
  const [navOpen, setNavOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === 'true')
  const palette = useCommandPalette()

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      localStorage.setItem(COLLAPSE_KEY, String(!current))
      return !current
    })
  }, [])

  // Ctrl/Cmd+B is the collapse shortcut everywhere else this pattern appears.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'b' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        toggleCollapsed()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggleCollapsed])

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex h-screen overflow-hidden bg-surface">
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[60] focus:rounded-md focus:bg-popover focus:px-3 focus:py-2 focus:text-sm focus:shadow-popover"
        >
          Skip to content
        </a>

        <Sidebar
          sections={nav}
          open={navOpen}
          onClose={() => setNavOpen(false)}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
        />

        <div className="flex min-w-0 flex-1 flex-col">
          <TopNav
            user={user}
            onOpenSearch={() => palette.setOpen(true)}
            onOpenNav={() => setNavOpen(true)}
            scopeControl={scopeControl}
            notifications={notifications}
          />

          {/* The only scroll container, so the sidebar and top bar stay put. */}
          <main id="main-content" className="flex-1 overflow-y-auto scrollbar-thin">
            <div className="mx-auto max-w-[1600px] p-4 lg:p-6">
              <RouteBreadcrumbs />
              {children}
            </div>
          </main>
        </div>
      </div>

      <CommandPalette open={palette.open} onOpenChange={palette.setOpen} />
    </TooltipProvider>
  )
}

/** Page heading with optional actions. Every page uses this so titles, spacing
 *  and action placement never drift between screens. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string
  description?: string
  actions?: ReactNode
}) {
  return (
    <div className="mb-4 mt-2 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">{title}</h1>
        {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
}
